/* import __COLOCATED_TEMPLATE__ from './headers.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { isPresent } from '@ember/utils';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import safeWindowOpen from 'embercom/lib/safe-window-open';

export default class Headers extends Component {
  @service store;
  @service router;
  @service intl;
  @service appService;

  @tracked isAddingAuthHeader;

  get isActionClass() {
    return this.args.headerClass === 'workflow-connector/action-header';
  }

  get isAuthClass() {
    return this.args.headerClass === 'custom-authentication/refresh-token-configuration-header';
  }

  get tokenFooterActions() {
    return [
      {
        text: this.intl.t('workflow-connector.builder.body.request.tabs.authentication.new-label'),
        icon: 'new',
        action: () =>
          safeWindowOpen(this.router.urlFor('apps.app.settings.app-settings.authentication.new')),
      },
      {
        text: this.intl.t(
          'workflow-connector.builder.body.request.tabs.authentication.manage-tokens',
        ),
        icon: 'settings',
        action: () =>
          safeWindowOpen(this.router.urlFor('apps.app.settings.app-settings.authentication')),
      },
    ];
  }

  get isMultiTokenEnabled() {
    return this.appService?.app?.canUseFeature('tdf-data-connectors-multi-token');
  }

  get addHeaderList() {
    return [
      {
        items: [
          {
            text: this.intl.t('workflow-connector.builder.body.request.key-value'),
            onSelectItem: () => this.addHeader(),
          },
          {
            text: this.intl.t('workflow-connector.builder.body.request.auth-token'),
            onSelectItem: () => {
              if (this.isMultiTokenEnabled) {
                this.args.onAddPendingToken();
              } else {
                this.startAddingAuthHeader();
              }
            },
          },
        ],
      },
    ];
  }

  get showAuthHeader() {
    if (this.isMultiTokenEnabled) {
      return false;
    }
    return isPresent(this.args.selectedToken) || this.isAddingAuthHeader;
  }

  get showIntercomVerifiedEmailHeader() {
    return this.args.headerObject.otpAuthEnabled;
  }

  get readonly() {
    return this.isStripeTokenSelected;
  }

  get isStripeTokenSelected() {
    return this.args.selectedToken?.createdBy === 'created_by_stripe_integration';
  }

  get includeAppAttributes() {
    return this.args.includeAppAttributes || false;
  }

  get localAttributes() {
    return this.args.localAttributes || [];
  }

  get isTemplatingFeatureEnabled() {
    return this.args.isTemplatingFeatureEnabled;
  }

  @action
  onTokenSelected(token) {
    this.args.updateWorkflowToken(token);
    if (this.isStripeTokenSelected) {
      this.args.headerObject.headers.clear();
    }
  }

  @action
  addHeader() {
    this.args.headerObject.headers.pushObject(
      this.store.createFragment(this.args.headerClass, {
        name: '',
        value: '',
      }),
    );
  }

  @action
  startAddingAuthHeader() {
    this.isAddingAuthHeader = true;
  }

  @action
  clearSelectedToken() {
    this.isAddingAuthHeader = false;
    this.args.clearSelectedToken();
  }

  @action
  deleteHeader(headerNameValue) {
    this.args.headerObject.headers.removeFragment(headerNameValue);
    this.refreshTemplatedParameters();
  }

  headerValueToBlocks(value) {
    return [
      {
        type: 'paragraph',
        text: value ?? '',
        modelKey: 'common/blocks/paragraph',
      },
    ];
  }

  @action
  onHeaderValueComposerChange(headerIndex, newBlocksDoc) {
    if (this.isTemplatingFeatureEnabled) {
      let blocks = newBlocksDoc.blocks;
      let processedBlocks = blocks[0]?.text || '';
      let header = this.args.headerObject.headers.objectAt(headerIndex);
      if (header) {
        header.set('value', processedBlocks.toString());
      }
      this.refreshTemplatedParameters();
    }
  }

  @action
  getHeaderValueBlocks(headerIndex) {
    let header = this.args.headerObject?.headers?.objectAt?.(headerIndex);
    if (!header) {
      return this.headerValueToBlocks('');
    }

    return this.headerValueToBlocks(header.value || '');
  }

  @action
  refreshTemplatedParameters() {
    if (this.isTemplatingFeatureEnabled && this.args.refreshTemplatedParameters) {
      this.args.refreshTemplatedParameters();
    }
  }

  isTokenReadonly(token) {
    return token?.createdBy === 'created_by_stripe_integration';
  }

  @action
  onTokenChange(index, item) {
    let tokenId = item?.value || item;
    this.args.onTokenChange(index, tokenId);
  }

  @action
  onRemoveToken(index) {
    this.args.onRemoveToken(index);
  }

  @action
  manageTokens() {
    safeWindowOpen(this.router.urlFor('apps.app.settings.app-settings.authentication'));
  }
}
